/**
 * Тонкий клиент к нашему worker REST API.
 * API_KEY хранится в localStorage — пользователь вставляет его при первом входе через /login.
 * Все запросы идут на ту же origin (nginx/Vite proxy → worker).
 */

const API_KEY_STORAGE = "astrov2.api_key";

export function getApiKey(): string | null {
  return localStorage.getItem(API_KEY_STORAGE);
}

export function setApiKey(key: string): void {
  localStorage.setItem(API_KEY_STORAGE, key);
}

export function clearApiKey(): void {
  localStorage.removeItem(API_KEY_STORAGE);
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

export async function api<T = unknown>(
  path: string,
  init?: RequestInit & { skipAuth?: boolean }
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!init?.skipAuth) {
    const key = getApiKey();
    if (key) headers.set("X-API-Key", key);
  }
  if (init?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const resp = await fetch(path, { ...init, headers });
  const text = await resp.text();
  let body: unknown = null;
  if (text) {
    try { body = JSON.parse(text); } catch { body = text; }
  }
  if (!resp.ok) {
    throw new ApiError(resp.status, `${resp.status}: ${typeof body === "string" ? body : resp.statusText}`, body);
  }
  return body as T;
}

// ----- Endpoints -----

export interface HealthResponse {
  status: string;
}

export interface ReadyResponse {
  status: string;
  db: string;
}

export interface PublicationLogEntry {
  id: number;
  channelCode: string;
  status: string;
  externalPostId?: string | null;
  externalUrl?: string | null;
  createdAt: string;
  publishedAt?: string | null;
  errorMessage?: string | null;
  /**
   * Что реально ушло в канал: "video" | "image" | "unknown".
   * "unknown" — у строк, созданных до появления маркера в payload_json;
   * это честнее, чем показывать "картинка" по умолчанию.
   */
  mediaKind?: string | null;
  /** "video_horizontal" | "video_vertical" */
  videoKind?: string | null;
  videoDurationSec?: number | null;
  videoWidth?: number | null;
  videoHeight?: number | null;
  videoAssetId?: number | null;
}

export interface DzenPublicationEntry {
  id: number;
  signCode: string;
  status: string;
  postId?: string | null;
  postUrl?: string | null;
  attemptCount: number;
  lastErrorKind?: string | null;
  lastError?: string | null;
  updatedAt: string;
  publishedAt?: string | null;
}

/** Ролик, сгенерированный на дату. null — видео-фабрика эту дату ещё не закрыла. */
export interface DayVideoInfo {
  assetId: number;
  kind: string;
  widthPx?: number | null;
  heightPx?: number | null;
  durationSec?: number | null;
  fileSizeBytes?: number | null;
  mimeType?: string | null;
  remotePath?: string | null;
  createdAt: string;
}

export interface DayStatusResponse {
  date: string;
  social: PublicationLogEntry[];
  dzen: DzenPublicationEntry[];
  /**
   * Отдельно от publication_log: показывает, что ролик ВООБЩЕ сгенерён, даже если
   * ни в один канал он ещё не уехал. Без этого «ролика нет» и «публикация не дошла»
   * выглядят одинаково, а лечатся по-разному.
   */
  dayVideo?: DayVideoInfo | null;
}

export interface ContentStatusSign {
  signCode: string;
  hasContent: boolean;
  archetype?: string | null;
  toneProfile?: string | null;
}

export interface ContentStatusDzen {
  signCode: string;
  hasContent: boolean;
  title?: string | null;
  hasCover: boolean;
  articleId?: number | null;
}

export interface ContentStatusBundle {
  hasContent: boolean;
  bundleId?: number | null;
  title?: string | null;
  intro?: string | null;
  hasCover: boolean;
}

/**
 * Combined-режим: объединённый Дзен-пост дня (sign_code='all') + AI-«посадочная дня» (landing).
 * Новый канонический Дзен-вывод — 1 пост/день вместо 12 пер-знак статей.
 */
export interface ContentStatusCombined {
  hasArticle: boolean;
  articleId?: number | null;
  title?: string | null;
  hasCover: boolean;
  hasLanding: boolean;
  landingTheme?: string | null;
  landingTitle?: string | null;
  landingIntro?: string | null;
  landingOutro?: string | null;
  landingHasCover: boolean;
  published: boolean;
  postUrl?: string | null;
}

export interface ContentStatusResponse {
  date: string;
  signDayCore: ContentStatusSign[];
  /** @deprecated пер-знак Дзен-статьи (источник/легаси). Канонический вывод теперь `combined`. */
  dzen: ContentStatusDzen[];
  combined: ContentStatusCombined;
  socialBundle: ContentStatusBundle;
}

export interface AiUsageRow {
  id: number;
  taskId: string;
  requestKind: string;
  modelResolved?: string | null;
  totalTokens?: number | null;
  totalUsd?: number | null;
  totalRub?: number | null;
  signCode?: string | null;
  relatedEntityType?: string | null;
  relatedEntityId?: number | null;
  createdAt: string;
}

export interface BackfillBucket {
  readyMin: number;
  totalMin: number;
  readyMax: number;
  totalMax: number;
  missingInMin: string[];
}

export interface BackfillStatusResponse {
  today: string;
  minHorizonDays: number;
  maxHorizonDays: number;
  signDayCore: BackfillBucket;
  dzenArticles: BackfillBucket;
  socialBundles: BackfillBucket;
  /** AI-«посадочная дня» Дзена (combined-режим, 1 пост/день): готова = Ready landing с обложкой. */
  dzenLandings: BackfillBucket;
  /**
   * Совместимость пар (sign_day_compatibility, Лунара). 78 пар = «полный» день.
   * pairsReady — суммарно сгенерированных пар (для прогресс-бара).
   * fullDays — дней где все 78 пар закрыты (юзер всегда получит из кеша, без AI).
   */
  compatibility: CompatibilityBucket;
}

export interface CompatibilityBucket {
  pairsReadyMin: number;
  pairsTotalMin: number;
  pairsReadyMax: number;
  pairsTotalMax: number;
  fullDaysMin: number;
  totalDaysMin: number;
  fullDaysMax: number;
  totalDaysMax: number;
  pairsPerDay: number;
  incompleteInMin: string[];
}

export interface BackfillRunReport {
  minHorizonDays: number;
  maxHorizonDays: number;
  concurrency: number;
  pipeline1Generated: number;
  dzenArticlesBuilt: number;
  bundlesGenerated: number;
  failed: number;
  totalCostRub: number;
  errorMessage: string | null;
}

export interface GenerationSettings {
  minHorizonDays: number;
  maxHorizonDays: number;
  backfillConcurrency: number;
  horizonDays: number;
  defaultLocale: string;
  primaryImageAspect: string;
}

export interface ActivityTask {
  id: number;
  kind: string;
  description: string;
  startedAt: string;
  completedAt?: string;
  durationSec: number;
  success?: boolean;
  forecastDate?: string | null;
  signCode?: string | null;
}

export interface AiHealthSnapshot {
  state: "Closed" | "Open" | "HalfOpen" | "ExtendedOpen";
  recentFailureCount: number;
  lastFailureReason?: string | null;
  lastFailureAt?: string | null;
  openUntil?: string | null;
  lastSuccessAt?: string | null;
  outageStartedAt?: string | null;
  isExtendedOutage: boolean;
  lastFailureCode?: string | null;
  proxiesHealthy?: number | null;
  proxiesTotal?: number | null;
  accountsHealthy?: number | null;
  accountsTotal?: number | null;
}

export interface ActivityResponse {
  activeCount: number;
  activeTasks: ActivityTask[];
  recentlyCompleted: ActivityTask[];
  aiHealth: AiHealthSnapshot;
}

// ----- Quality / Repair -----

export interface QualityIssueItem {
  id: number;
  forecastDate: string;
  signCode: string;
  issues: string[];
  contentVersion: number;
}

export interface QualityInspectResponse {
  from: string;
  to: string;
  inspectedRecords: number;
  lowQualityCount: number;
  issueCounts: Record<string, number>;
  lowQualityItems: QualityIssueItem[];
}

export interface QualityRepairResponse {
  from: string;
  to: string;
  lowQualityFound: number;
  repaired: number;
  failed: number;
  totalCostRub: number;
  durationSeconds?: number | null;
}

export interface RepairOneResponse {
  id: number;
  forecastDate: string;
  signCode: string;
  success: boolean;
  skipped: boolean;
  durationMs: number;
  costRub: number;
  error?: string | null;
}

export const Api = {
  health: () => api<HealthResponse>("/health", { skipAuth: true }),
  ready: () => api<ReadyResponse>("/ready", { skipAuth: true }),
  statusToday: () => api<DayStatusResponse>("/api/status/today"),
  publications: (date: string) =>
    api<DayStatusResponse>(`/api/publications?date=${date}`),
  contentStatus: (date: string) =>
    api<ContentStatusResponse>(`/api/content-status?date=${date}`),
  backfillStatus: () => api<BackfillStatusResponse>("/api/backfill/status"),
  backfillRun: () => api<BackfillRunReport>("/api/backfill/run", { method: "POST" }),
  backfillActivity: () => api<ActivityResponse>("/api/backfill/activity"),
  generationSettings: () => api<GenerationSettings>("/api/generation-settings"),
  updateGenerationSettings: (body: { minHorizonDays: number; maxHorizonDays: number; backfillConcurrency: number }) =>
    api<GenerationSettings>("/api/generation-settings", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // Качество данных (sign_day_core repair)
  qualityInspect: (from: string, to: string) =>
    api<QualityInspectResponse>(`/api/repair/sign-day-core/inspect?from=${from}&to=${to}`),
  qualityRepair: (from: string, to: string, max: number = 100) =>
    api<QualityRepairResponse>(
      `/api/repair/sign-day-core/run?from=${from}&to=${to}&max=${max}`,
      { method: "POST" }
    ),
  qualityRepairOne: (id: number, signal?: AbortSignal) =>
    api<RepairOneResponse>(
      `/api/repair/sign-day-core/repair-one?id=${id}`,
      { method: "POST", signal }
    ),

  // Триггеры
  triggerGenerate: (date: string) =>
    api<unknown>(`/api/jobs/generate?date=${date}`, { method: "POST" }),
  triggerSignForecasts: (date: string, sign?: string) =>
    api<unknown>(
      `/api/jobs/generate-sign-forecasts?date=${date}${sign ? `&sign=${sign}` : ""}`,
      { method: "POST" }
    ),
  triggerBuildDzen: (date: string, sign?: string) =>
    api<unknown>(
      `/api/jobs/build-dzen-articles?date=${date}${sign ? `&sign=${sign}` : ""}`,
      { method: "POST" }
    ),
  triggerPublish: (date: string, channels?: string, sign?: string) => {
    const params = new URLSearchParams({ date });
    if (channels) params.set("channels", channels);
    if (sign) params.set("sign", sign);
    return api<unknown>(`/api/jobs/publish?${params.toString()}`, { method: "POST" });
  },
  retryFailed: () => api<unknown>("/api/jobs/retry-failed", { method: "POST" }),

  // Self-hosted error tracking (T3)
  errorsList: (q: {
    source?: string; resolved?: boolean; fromHours?: number; search?: string;
    limit?: number; offset?: number;
  }) => {
    const params = new URLSearchParams();
    if (q.source) params.set("source", q.source);
    if (q.resolved !== undefined) params.set("resolved", String(q.resolved));
    if (q.fromHours !== undefined) params.set("from_hours", String(q.fromHours));
    if (q.search) params.set("q", q.search);
    if (q.limit) params.set("limit", String(q.limit));
    if (q.offset) params.set("offset", String(q.offset));
    return api<{ items: ErrorFingerprintRow[] }>(`/api/admin/errors/?${params.toString()}`);
  },
  errorEvents: (fingerprint: string, limit = 20) =>
    api<{ items: ErrorEventDetail[] }>(`/api/admin/errors/${fingerprint}/events?limit=${limit}`),
  errorEventById: (id: number) =>
    api<ErrorEventDetail>(`/api/admin/errors/event/${id}`),
  errorResolve: (fingerprint: string) =>
    api<unknown>(`/api/admin/errors/${fingerprint}/resolve`, { method: "POST" }),
  errorStats: () => api<ErrorStats>("/api/admin/errors/stats"),
};

// ----- Error tracking types -----

export interface ErrorFingerprintRow {
  fingerprint: string;
  source: string;
  errorType: string | null;
  sampleMessage: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  totalCount: number;
  resolvedAt: string | null;
  resolvedCount: number;
  events24h: number;
}

export interface ErrorEventDetail {
  id: number;
  fingerprint: string;
  createdAt: string;
  level: string;
  message: string;
  stackTrace: string | null;
  stackTraceSymbolicated: string | null;
  url: string | null;
  userAgent: string | null;
  appUserId: number | null;
  appVersion: string | null;
  context: string | null;
}

export interface ErrorDailyBucket {
  day: string;
  count: number;
}

export interface ErrorStats {
  total24h: number;
  total7d: number;
  activeFingerprints: number;
  newFingerprints24h: number;
  regressions24h: number;
  dailyBuckets: ErrorDailyBucket[];
}
